import React, { useState, useEffect } from 'react';
import apiClient from '../../services/apiClient';
import StakeTable from './StakeTable';
import { Button, Badge, Modal, FormInput, Toggle } from '../../components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface Aggregator {
  id: number;
  name: string;
  developer_is_enabled: boolean;
  admin_is_enabled: boolean;
  api_settings: any;
}

const fetchAggregatorsAPI = async (): Promise<Aggregator[]> => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/game-engine/admin/aggregators`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const fetchCurrenciesAPI = async (): Promise<any[]> => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/currencies`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.currencies : [];
};

const Aggregators: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(50);

  const { data: aggregators = [], isLoading } = useQuery({
    queryKey: ['aggregators'],
    queryFn: fetchAggregatorsAPI
  });

  const { data: activeCurrencies = [] } = useQuery({
    queryKey: ['currencies'],
    queryFn: fetchCurrenciesAPI
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Aggregator>>({});
  const [isEdit, setIsEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Custom UI states for setup
  const [setupType, setSetupType] = useState<'unified' | 'separate'>('unified');
  const [supportedCurrencies, setSupportedCurrencies] = useState<string[]>([]);

  // Password Protection State
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [pendingAction, setPendingAction] = useState<() => void>(() => { });

  const requirePassword = (action: () => void) => {
    setPendingAction(() => action);
    setPasswordInput('');
    setIsPasswordModalOpen(true);
  };

  const verifyPassword = () => {
    if (passwordInput === 'Metablock@123##') {
      setIsPasswordModalOpen(false);
      pendingAction();
    } else {
      toast.error('Incorrect password');
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Aggregator>) => {
      const token = localStorage.getItem("token");

      let finalData = { ...data } as any;
      const isToggle = finalData._isToggle;
      delete finalData._isToggle;

      if (!isToggle) {
        let parsedSettings;
        if (typeof finalData.api_settings === 'string' && finalData.api_settings.trim() !== '') {
          try {
            parsedSettings = JSON.parse(finalData.api_settings);
          } catch (e) {
            throw new Error('Invalid JSON format in API Settings');
          }
        } else if (typeof finalData.api_settings === 'object') {
          parsedSettings = finalData.api_settings;
        }

        // If unified, handle the injection of currencies and ensure it's an array
        if (setupType === 'unified') {
          if (!parsedSettings) parsedSettings = {};

          let settingsObj = Array.isArray(parsedSettings) ? parsedSettings[0] : parsedSettings;
          if (!settingsObj) settingsObj = {};

          if (supportedCurrencies && supportedCurrencies.length > 0) {
            settingsObj.supported_currencies = supportedCurrencies;
          }

          finalData.api_settings = [settingsObj]; // ALWAYS SAVE AS ARRAY
        } else {
          // If separate, it MUST be an array
          if (!Array.isArray(parsedSettings)) {
            throw new Error("For Separate Accounts, API Settings must be a JSON Array [...]");
          }
          finalData.api_settings = parsedSettings;
        }
      }

      if (data.id) {
        return apiClient.put(`/api/game-engine/admin/aggregators/${data.id}`, finalData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        return apiClient.post(`/api/game-engine/admin/aggregators`, finalData, { headers: { Authorization: `Bearer ${token}` } });
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['aggregators'] });
      toast.success(variables.id ? "Aggregator updated!" : "Aggregator created!");
      closeModal();
    },
    onError: (e: any) => toast.error(e.message || "Failed to save aggregator")
  });

  const openModal = (agg?: Aggregator) => {
    requirePassword(() => {
      if (agg) {
        let parsed = agg.api_settings;

        // If it's an array, determine setupType
        let isArr = Array.isArray(parsed);

        if (isArr && parsed.length > 0 && parsed[0].supported_currencies) {
          // It's an array but it uses the unified structure
          setSetupType('unified');
          setSupportedCurrencies(parsed[0].supported_currencies);
          setFormData({
            ...agg,
            api_settings: JSON.stringify(parsed[0], null, 2)
          });
        } else if (isArr && parsed.length > 0) {
          // It's a multiple accounts array
          setSetupType('separate');
          const extractedCurrencies = parsed.map((p: any) => p.currency).filter(Boolean);
          setSupportedCurrencies(extractedCurrencies);
          setFormData({
            ...agg,
            api_settings: JSON.stringify(parsed, null, 2)
          });
        } else {
          // Fallback
          setSetupType('unified');
          setSupportedCurrencies(parsed?.supported_currencies || []);
          setFormData({
            ...agg,
            api_settings: parsed ? JSON.stringify(parsed, null, 2) : ''
          });
        }
        setIsEdit(true);
      }
      else {
        setSetupType('unified');
        setSupportedCurrencies([]);
        setFormData({ name: '', developer_is_enabled: true, admin_is_enabled: true, api_settings: '' });
        setIsEdit(false);
      }
      setIsModalOpen(true);
    });
  };

  const closeModal = () => { setIsModalOpen(false); setFormData({}); };
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); saveMutation.mutate(formData); };

  const handleToggleDeveloperStatus = (agg: Aggregator, checked: boolean) => {
    requirePassword(() => {
      saveMutation.mutate({ ...agg, developer_is_enabled: checked, _isToggle: true } as any);
    });
  };

  const handleToggleAdminStatus = (agg: Aggregator, checked: boolean) => {
    saveMutation.mutate({ ...agg, admin_is_enabled: checked, _isToggle: true } as any);
  };

  const filtered = aggregators.filter((a: any) => !searchTerm || a.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const paginated = filtered.slice((page - 1) * limit, page * limit);

  const columns = [
    { key: 'id', label: 'ID', width: 'w-16' },
    { key: 'name', label: 'Name' },
    {
      key: 'developer_is_enabled', label: 'Developer Status',
      render: (val: boolean, item: Aggregator) => (
        <Toggle checked={val} onChange={(c) => handleToggleDeveloperStatus(item, c)} />
      )
    },
    {
      key: 'admin_is_enabled', label: 'Admin Status',
      render: (val: boolean, item: Aggregator) => (
        <Toggle checked={val} onChange={(c) => handleToggleAdminStatus(item, c)} />
      )
    },
    {
      key: 'actions', label: 'Actions', textAlign: 'right' as const,
      render: (_: any, item: Aggregator) => (
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="xs" onClick={() => openModal(item)}>Edit</Button>
        </div>
      )
    }
  ];

  return (
    <>
      <StakeTable
        title="Game Aggregators"
        columns={columns}
        data={paginated}
        isLoading={isLoading}
        searchPlaceholder="Search aggregators..."
        onSearch={(q) => { setSearchTerm(q); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={filtered.length}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        emptyMessage="No aggregators found."
        actions={<Button variant="success" onClick={() => openModal()}>+ Add Aggregator</Button>}
      />

      <Modal
        open={isModalOpen}
        onClose={closeModal}
        title={isEdit ? 'Edit Aggregator' : 'Add Aggregator'}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeModal}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saveMutation.isPending} onClick={handleSubmit}>
              {isEdit ? 'Save Changes' : 'Create'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Name" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-300">Wallet Setup Type</label>
            <select
              className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg p-2.5 text-sm outline-none focus:border-indigo-500 transition-all"
              value={setupType}
              onChange={(e) => setSetupType(e.target.value as any)}
            >
              <option value="unified">Unified Account (Single credentials, Multi-currency)</option>
              <option value="separate">Separate Accounts (Different credentials per currency)</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">Supported Currencies</label>
            <div className="flex flex-wrap gap-3 p-3 bg-slate-900 border border-slate-700 rounded-lg max-h-40 overflow-y-auto">
              {activeCurrencies.map((c: any) => (
                <label key={c.code} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer hover:text-white">
                  <input
                    type="checkbox"
                    className="rounded bg-slate-800 border-slate-600 text-indigo-500 focus:ring-indigo-500"
                    checked={supportedCurrencies.includes(c.code.toUpperCase())}
                    onChange={(e) => {
                      let newSelected = [...supportedCurrencies];
                      const curr = c.code.toUpperCase();
                      if (e.target.checked) {
                        newSelected.push(curr);
                      } else {
                        newSelected = newSelected.filter(sc => sc !== curr);
                      }
                      setSupportedCurrencies(newSelected);

                      // Auto-generate JSON template logic for separate accounts
                      if (setupType === 'separate') {
                        let currentParsed = [];
                        try {
                          currentParsed = JSON.parse(formData.api_settings || '[]');
                        } catch (err) { }
                        
                        // Keep objects that are still selected
                        let newParsed = currentParsed.filter((obj: any) => newSelected.includes(obj.currency));
                        
                        // Add new objects for newly selected
                        newSelected.forEach(curr => {
                          if (!newParsed.find((obj: any) => obj.currency === curr)) {
                            newParsed.push({
                              api_base_url: "https://ps9games.com",
                              agent_code: "",
                              token: "",
                              secret_key: "",
                              currency: curr
                            });
                          }
                        });
                        
                        setFormData({ ...formData, api_settings: JSON.stringify(newParsed, null, 2) });
                      }
                    }}
                  />
                  {c.code.toUpperCase()}
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400">Select active currencies this aggregator supports.</p>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-300">
              {setupType === 'unified' ? 'API Settings (JSON Object)' : 'API Settings (JSON Array)'}
            </label>
            <textarea
              className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg p-3 text-sm font-mono h-48 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all resize-y"
              value={formData.api_settings || ''}
              onChange={e => setFormData({ ...formData, api_settings: e.target.value })}
              placeholder={setupType === 'unified'
                ? '{\n  "api_base_url": "https://...",\n  "api_key": "..."\n}'
                : '[\n  {\n    "api_base_url": "https://...",\n    "currency": "INR"\n  },\n  {\n    "api_base_url": "https://...",\n    "currency": "USD"\n  }\n]'}
            />
            
            {/* Dynamic Example Config based on Aggregator Name */}
            {formData.name && formData.name.toLowerCase().includes('wc') && (
              <div className="mt-2 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <p className="text-xs text-slate-400 mb-2 font-semibold">Example Config for WC ({setupType === 'unified' ? 'Unified Account' : 'Separate Accounts'}):</p>
                {setupType === 'separate' ? (
                  <pre className="text-xs text-indigo-300 font-mono whitespace-pre-wrap">
{`[
  {
    "api_base_url": "https://ps9games.com",
    "agent_code": "MTB001H",
    "token": "<YOUR_TOKEN>",
    "secret_key": "<YOUR_SECRET>",
    "currency": "USD"
  },
  {
    "api_base_url": "https://ps9games.com",
    "agent_code": "MTB00H8",
    "token": "<YOUR_TOKEN_2>",
    "secret_key": "<YOUR_SECRET_2>",
    "currency": "INR"
  }
]`}
                  </pre>
                ) : (
                  <pre className="text-xs text-indigo-300 font-mono whitespace-pre-wrap">
{`{
  "api_base_url": "https://ps9games.com",
  "agent_code": "MTB001H",
  "token": "<YOUR_TOKEN>",
  "secret_key": "<YOUR_SECRET>",
  "supported_currencies": ["USD", "INR"]
}`}
                  </pre>
                )}
              </div>
            )}
            {/* You can add more aggregator examples here in the future like this: */}
            {/* {formData.name && formData.name.toLowerCase().includes('evolution') && ( ... )} */}
          </div>

          <div className="pt-2 border-t border-slate-800">
            <Toggle
              label="Developer Status"
              checked={!!formData.developer_is_enabled}
              onChange={(val) => setFormData({ ...formData, developer_is_enabled: val })}
            />
          </div>
        </form>
      </Modal>

      {/* Password Protection Modal */}
      <Modal open={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} title="Security Verification"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsPasswordModalOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={verifyPassword}>Verify</Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-400">This action requires developer authorization.</p>
          <input type="text" autoComplete="username" style={{ position: 'absolute', opacity: 0, top: '-9999px' }} />
          <FormInput type="password" label="Developer Password" value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && verifyPassword()} autoFocus autoComplete="new-password" />
        </div>
      </Modal>
    </>
  );
};
export default Aggregators;
